// SeoApplyPrimitives.jsx — extracted primitives from SeoApplyDashboard.jsx
// Phase 27c Housekeeping (F.1) · frontend split to keep main dashboard < 800 lines
// Zero functionality change · zero prop-shape change · public API is exactly
// what the main dashboard consumed inline before.

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    CheckCircle2, ChevronDown, ChevronRight, Clock, Info, Loader2, Lock,
    RotateCcw, ShieldAlert, Wrench, X, Zap,
} from "lucide-react";

// ---------- Small primitives ------------------------------------------------

export function KpiCard({ icon: Icon, label, value, tone = "primary", testid, hint }) {
    const toneClass = {
        primary: "text-[var(--color-primary)]",
        success: "text-[var(--color-success)]",
        alert: "text-[var(--color-alert)]",
        warning: "text-[#A3A3A3]",
        secondary: "text-[var(--color-secondary)]",
    }[tone];
    return (
        <div
            data-testid={testid}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
        >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-2">
                <Icon className={`h-3 w-3 ${toneClass}`} /> {label}
            </div>
            <div className={`text-2xl font-semibold tabular-nums ${toneClass}`}>
                {value}
            </div>
            {hint && (
                <p className="text-[10px] text-text-secondary mt-1 leading-tight">
                    {hint}
                </p>
            )}
        </div>
    );
}

export function TypeChip({ kind }) {
    const cls = {
        variant: "text-white bg-white/5 border-white/15",
        internal_link: "text-white bg-white/5 border-white/15",
        external_link: "text-[#A3A3A3] bg-[#A3A3A3]/10 border-[#A3A3A3]/30",
        schema_markup: "text-white bg-white/5 border-white/15",
    }[kind] || "text-text-secondary bg-white/5 border-white/10";
    return (
        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}>
            {String(kind || "").replace("_", " ")}
        </span>
    );
}


// ---------- Kill-switch banner ---------------------------------------------

export function KillSwitchBanner({ enabled, rollbackEnabled, rateLimit, isAdmin, onToggle, busy }) {
    const off = !enabled;
    const wrapperCls = off
        ? "border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10"
        : "border-[#A3A3A3]/40 bg-[#A3A3A3]/10";
    const Icon = off ? ShieldAlert : Zap;
    const iconCls = off ? "text-[var(--color-alert)]" : "text-[#A3A3A3]";
    const label = off ? "DISABLED" : "LIVE";
    const message = off
        ? "SEO Apply Layer is DISABLED. All apply endpoints return 503. Real content mutations are blocked."
        : `SEO Apply Layer is LIVE. Real mutations are enabled on whitelisted fields only. Rate limit: ${rateLimit || 5} applies/day/admin. Chain-safe rollback: ${rollbackEnabled ? "ON" : "OFF"}.`;

    return (
        <div
            data-testid="seo-apply-killswitch-banner"
            className={`rounded-2xl border ${wrapperCls} p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-3 mb-6`}
        >
            <Icon className={`h-6 w-6 shrink-0 ${iconCls}`} />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-widest text-text-secondary">
                        SEO_APPLY_ENABLED ·
                    </span>
                    <span
                        data-testid="seo-apply-killswitch-state"
                        className={`text-[11px] font-semibold ${off ? "text-[var(--color-alert)]" : "text-[#A3A3A3]"}`}
                    >
                        {label}
                    </span>
                </div>
                <p className="text-xs text-text-secondary">{message}</p>
            </div>
            {isAdmin && (
                <button
                    data-testid="seo-apply-killswitch-toggle"
                    onClick={onToggle}
                    disabled={busy}
                    className={`text-xs rounded-full px-4 py-2 font-medium shrink-0 flex items-center gap-2 disabled:opacity-50 ${
                        off
                            ? "bg-[var(--color-alert)] hover:bg-[var(--color-alert)]/90 text-white"
                            : "border border-[var(--color-border)] text-text-secondary hover:text-text-main"
                    }`}
                >
                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : off ? <Zap className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />}
                    {off ? "Enable now" : "Disable"}
                </button>
            )}
        </div>
    );
}


// ---------- Field diff renderer --------------------------------------------

function isScalar(v) {
    return v == null || typeof v === "string" || typeof v === "number" || typeof v === "boolean";
}

export function SchemaDiff({ before, after }) {
    const b = JSON.stringify(before ?? null, null, 2);
    const a = JSON.stringify(after ?? null, null, 2);
    return (
        <div
            data-testid="seo-apply-diff-schema"
            className="grid grid-cols-1 md:grid-cols-2 gap-2"
        >
            <div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--color-alert)] mb-1">Before (JSON-LD)</div>
                <pre className="text-[11px] whitespace-pre-wrap break-words text-[var(--color-alert)]/90 bg-[var(--color-bg)] border border-[var(--color-alert)]/30 rounded-lg p-3 max-h-80 overflow-auto font-mono">
                    {b || "null"}
                </pre>
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-widest text-white mb-1">After (JSON-LD)</div>
                <pre className="text-[11px] whitespace-pre-wrap break-words text-white/90 bg-[var(--color-bg)] border border-white/15 rounded-lg p-3 max-h-80 overflow-auto font-mono">
                    {a || "null"}
                </pre>
            </div>
        </div>
    );
}

export function ScalarDiff({ before, after }) {
    return (
        <div className="space-y-1">
            {before != null && String(before) !== "" && (
                <div className="rounded-lg border border-[var(--color-alert)]/30 bg-[var(--color-alert)]/5 px-3 py-2">
                    <span className="text-[10px] uppercase tracking-widest text-[var(--color-alert)] mr-2">−</span>
                    <span className="text-xs text-[var(--color-alert)]/90 line-through decoration-[var(--color-alert)]/60 break-words">
                        {String(before)}
                    </span>
                </div>
            )}
            {after != null && String(after) !== "" && (
                <div className="rounded-lg border border-white/15 bg-white/5 px-3 py-2">
                    <span className="text-[10px] uppercase tracking-widest text-white mr-2">+</span>
                    <span className="text-xs text-white/90 break-words">
                        {String(after)}
                    </span>
                </div>
            )}
            {(before == null || String(before) === "") && (after == null || String(after) === "") && (
                <p className="text-[11px] text-text-secondary italic">(empty on both sides)</p>
            )}
        </div>
    );
}

export function ListDiff({ before, after, keyProp }) {
    const beforeArr = Array.isArray(before) ? before : [];
    const afterArr = Array.isArray(after) ? after : [];
    const beforeKeys = new Set(beforeArr.map((x) => (typeof x === "object" ? JSON.stringify(x[keyProp] ?? x) : String(x))));
    const afterKeys = new Set(afterArr.map((x) => (typeof x === "object" ? JSON.stringify(x[keyProp] ?? x) : String(x))));
    const rendered = (x) => {
        if (typeof x === "object" && x !== null) {
            const label = x.target_slug || x.domain_hint || x.anchor_text || JSON.stringify(x);
            const meta = x.anchor_text && x.target_slug ? ` — “${x.anchor_text}”` : "";
            return `${label}${meta}`;
        }
        return String(x);
    };
    return (
        <div className="space-y-1">
            {beforeArr.map((x, i) => {
                const k = typeof x === "object" ? JSON.stringify(x[keyProp] ?? x) : String(x);
                const removed = !afterKeys.has(k);
                return (
                    <div
                        key={`b-${i}`}
                        className={`rounded-lg px-3 py-2 border text-xs break-words ${
                            removed
                                ? "border-[var(--color-alert)]/30 bg-[var(--color-alert)]/5 text-[var(--color-alert)]/90 line-through decoration-[var(--color-alert)]/60"
                                : "border-[var(--color-border)] bg-white/5 text-text-secondary"
                        }`}
                    >
                        <span className="text-[10px] uppercase tracking-widest mr-2 opacity-70">
                            {removed ? "−" : "="}
                        </span>
                        {rendered(x)}
                    </div>
                );
            })}
            {afterArr.map((x, i) => {
                const k = typeof x === "object" ? JSON.stringify(x[keyProp] ?? x) : String(x);
                if (beforeKeys.has(k)) return null;
                return (
                    <div
                        key={`a-${i}`}
                        className="rounded-lg px-3 py-2 border border-white/15 bg-white/5 text-xs text-white/90 break-words"
                    >
                        <span className="text-[10px] uppercase tracking-widest mr-2 opacity-70">+</span>
                        {rendered(x)}
                    </div>
                );
            })}
            {beforeArr.length === 0 && afterArr.length === 0 && (
                <p className="text-[11px] text-text-secondary italic">(empty on both sides)</p>
            )}
        </div>
    );
}

export function FieldDiff({ field, before, after }) {
    let body;
    if (field === "schema_markup") {
        body = <SchemaDiff before={before} after={after} />;
    } else if (field === "applied_internal_links") {
        body = <ListDiff before={before} after={after} keyProp="target_slug" />;
    } else if (field === "applied_external_links") {
        body = <ListDiff before={before} after={after} keyProp="domain_hint" />;
    } else if (field === "h2_outline") {
        body = <ListDiff before={before} after={after} />;
    } else if (isScalar(before) && isScalar(after)) {
        body = <ScalarDiff before={before} after={after} />;
    } else {
        body = <SchemaDiff before={before} after={after} />;
    }
    return (
        <div
            data-testid={`seo-apply-diff-field-${field}`}
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-3"
        >
            <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-2 font-mono">
                {field}
            </div>
            {body}
        </div>
    );
}

export function DiffPreview({ fields, previousValues, newValues }) {
    if (!fields || fields.length === 0) {
        return (
            <p className="text-xs text-text-secondary italic">
                No field changes to display.
            </p>
        );
    }
    return (
        <div className="space-y-3">
            {fields.map((f) => (
                <FieldDiff
                    key={f}
                    field={f}
                    before={(previousValues || {})[f]}
                    after={(newValues || {})[f]}
                />
            ))}
        </div>
    );
}


// ---------- Confirm modal ---------------------------------------------------

export function ConfirmModal({ open, title, subtitle, tone = "primary", confirmLabel,
                        onConfirm, onCancel, busy, children }) {
    const toneCls = {
        primary: "bg-white hover:bg-[#EDEDED] text-black",
        alert: "bg-[var(--color-alert)] hover:bg-[var(--color-alert)]/90 text-white",
        warning: "bg-[#A3A3A3] hover:bg-[#A3A3A3]/90 text-black",
    }[tone];
    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
                        onClick={busy ? undefined : onCancel}
                    />
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.98 }}
                        transition={{ duration: 0.18 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                    >
                        <div
                            data-testid="seo-apply-confirm-modal"
                            className="pointer-events-auto w-full max-w-3xl rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
                        >
                            <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--color-border)]">
                                <div>
                                    <h3 className="text-base md:text-lg font-semibold text-text-main">
                                        {title}
                                    </h3>
                                    {subtitle && (
                                        <p className="text-xs text-text-secondary mt-1">{subtitle}</p>
                                    )}
                                </div>
                                <button
                                    data-testid="seo-apply-confirm-close"
                                    onClick={onCancel}
                                    disabled={busy}
                                    className="text-text-secondary hover:text-text-main disabled:opacity-40"
                                    aria-label="Close"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="p-5 overflow-y-auto flex-1">
                                {children}
                            </div>
                            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--color-border)] bg-[var(--color-bg)]/40">
                                <button
                                    data-testid="seo-apply-confirm-cancel"
                                    onClick={onCancel}
                                    disabled={busy}
                                    className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 disabled:opacity-40"
                                >
                                    Cancel
                                </button>
                                <button
                                    data-testid="seo-apply-confirm-ok"
                                    onClick={onConfirm}
                                    disabled={busy}
                                    className={`text-xs rounded-full px-5 py-2 font-medium flex items-center gap-2 disabled:opacity-50 ${toneCls}`}
                                >
                                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                                    {confirmLabel}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}


// ---------- Suggestion row + log row ---------------------------------------

export function SuggestionRow({ suggestion, onApply, disabled }) {
    const kind = ({
        variant_wrapper: "variant",
        internal_linking_engine: "internal_link",
        external_linking_engine: "external_link",
        repurposing_wrapper: "schema_markup",
    })[suggestion.engine_source] || suggestion.engine_source;
    const payload = suggestion.payload || {};
    const title = (
        payload.seo_title_suggested
        || payload.anchor_text_suggested
        || payload.outbound_domain_hint
        || payload.h1_suggested
        || payload.pillar_topic
        || (payload.schema_markup_normalised && `Schema: ${payload.schema_markup_normalised["@type"] || "object"}`)
        || suggestion.engine_source
    );
    const asset = payload.target_asset_type
        ? `${payload.target_asset_type} · ${(payload.target_asset_id || "").slice(0, 8)}`
        : "";
    return (
        <li
            data-testid={`seo-apply-suggestion-${suggestion.id}`}
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-4 py-3 flex items-start gap-3"
        >
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <TypeChip kind={kind} />
                    <span className="text-[10px] uppercase tracking-widest text-[var(--color-success)]">
                        acknowledged
                    </span>
                    {asset && (
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary font-mono">
                            {asset}
                        </span>
                    )}
                </div>
                <div className="text-sm text-text-main font-medium truncate">
                    {String(title || "(no title)").slice(0, 160)}
                </div>
                {suggestion.reasoning && (
                    <p className="text-[11px] text-text-secondary italic mt-1 line-clamp-2">
                        {suggestion.reasoning}
                    </p>
                )}
            </div>
            <button
                data-testid={`seo-apply-btn-${suggestion.id}`}
                onClick={() => onApply(suggestion, kind)}
                disabled={disabled}
                className="shrink-0 text-[11px] rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2 font-medium flex items-center gap-1 disabled:opacity-50"
            >
                <Wrench className="h-3 w-3" /> Apply
            </button>
        </li>
    );
}

export function ApplyLogRow({ log, onRollback, onView, disabled }) {
    const [expanded, setExpanded] = useState(false);
    const now = new Date();
    const cutoff = log.rollback_available_until ? new Date(log.rollback_available_until) : null;
    const expired = !cutoff || now > cutoff;
    const rolledBack = !!log.rolled_back;
    const canRollback = !rolledBack && !expired;
    const remainingHours = cutoff ? Math.max(0, Math.round((cutoff - now) / 3600000)) : 0;

    return (
        <li
            data-testid={`seo-apply-log-${log.apply_id}`}
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-4 py-3"
        >
            <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <TypeChip kind={log.applicable_type} />
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">
                            {log.asset_type} · {(log.asset_id || "").slice(0, 8)}
                        </span>
                        {rolledBack ? (
                            <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3] flex items-center gap-1">
                                <RotateCcw className="h-3 w-3" /> rolled back
                            </span>
                        ) : expired ? (
                            <span className="text-[10px] uppercase tracking-widest text-text-secondary flex items-center gap-1">
                                <Lock className="h-3 w-3" /> rollback window expired
                            </span>
                        ) : (
                            <span className="text-[10px] uppercase tracking-widest text-[var(--color-success)] flex items-center gap-1">
                                <Clock className="h-3 w-3" /> {remainingHours}h left
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-text-secondary flex flex-wrap items-center gap-2">
                        <span className="font-mono">{(log.applied_at || "").slice(0, 19).replace("T", " ")} UTC</span>
                        <span>·</span>
                        <span>fields:</span>
                        {(log.fields_changed || []).map((f) => (
                            <span
                                key={f}
                                className="text-[10px] px-1.5 py-0.5 rounded border border-[var(--color-border)] text-text-main font-mono"
                            >
                                {f}
                            </span>
                        ))}
                    </div>
                    {log.admin_email && (
                        <p className="text-[11px] text-text-secondary mt-1">
                            by {log.admin_email}
                        </p>
                    )}
                    <button
                        onClick={() => setExpanded((v) => !v)}
                        data-testid={`seo-apply-log-toggle-${log.apply_id}`}
                        className="text-[10px] uppercase tracking-widest text-[var(--color-primary)] mt-2 flex items-center gap-1"
                    >
                        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        {expanded ? "Hide diff" : "Show diff"}
                    </button>
                    <AnimatePresence initial={false}>
                        {expanded && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.16 }}
                                className="overflow-hidden"
                            >
                                <div className="mt-3">
                                    <DiffPreview
                                        fields={log.fields_changed || []}
                                        previousValues={log.previous_values}
                                        newValues={log.new_values}
                                    />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                    {rolledBack && log.rollback_report && (
                        <div className="mt-2 rounded-lg border border-[#A3A3A3]/30 bg-[#A3A3A3]/5 px-3 py-2 text-[11px] text-[#A3A3A3]/90">
                            Rolled back at {(log.rolled_back_at || "").slice(0, 19).replace("T", " ")} ·{" "}
                            restored {log.rollback_report.restored_count || 0} ·{" "}
                            skipped {log.rollback_report.skipped_count || 0}
                            {(log.rollback_report.skipped_fields || []).length > 0 && (
                                <div className="mt-1 opacity-80">
                                    Skipped: {log.rollback_report.skipped_fields.map((s) => `${s.field}(${s.reason})`).join(", ")}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                    <button
                        data-testid={`seo-apply-rollback-btn-${log.apply_id}`}
                        disabled={!canRollback || disabled}
                        onClick={() => onRollback(log)}
                        className="text-[10px] rounded-full border border-[#A3A3A3]/40 text-[#A3A3A3] px-3 py-1 hover:bg-[#A3A3A3]/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                        <RotateCcw className="h-3 w-3" />
                        Rollback
                    </button>
                    <button
                        data-testid={`seo-apply-view-btn-${log.apply_id}`}
                        onClick={() => onView(log)}
                        className="text-[10px] rounded-full border border-[var(--color-border)] text-text-secondary px-3 py-1 hover:text-text-main flex items-center gap-1"
                    >
                        <Info className="h-3 w-3" />
                        Details
                    </button>
                </div>
            </div>
        </li>
    );
}
